import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { get, post } from "@/lib/api";
import { Plus, CalendarDays } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { PageHeader, Section } from "@/components/common/PageHeader";
import { Skeleton } from "@/components/common/EmptyState";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Avatar } from "@/components/common/Avatar";
import { Input } from "@/components/ui/input";
import { SearchInput } from "@/components/common/SearchInput";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { money, dateFmt } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function Leave() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { can, me } = useApp();
  const { data, isLoading } = useQuery({ queryKey: ["leave"], queryFn: () => get("/hrms/leave") });
  const { data: users = [] } = useQuery({ queryKey: ["users"], queryFn: () => get("/users") });
  const umap = useMemo(() => Object.fromEntries(users.map((u) => [u.id, u])), [users]);
  const [rejecting, setRejecting] = useState(null);
  const [note, setNote] = useState("");
  const [leaveSearch, setLeaveSearch] = useState("");
  const [leaveStatusFilter, setLeaveStatusFilter] = useState("all");
  const [reimbSearch, setReimbSearch] = useState("");
  const [reimbStatusFilter, setReimbStatusFilter] = useState("all");

  const leaveStatuses = useMemo(
    () => Array.from(new Set((data?.applications || []).map((a) => a.status).filter(Boolean))).sort(),
    [data]
  );
  const reimbStatuses = useMemo(
    () => Array.from(new Set((data?.reimbursements || []).map((r) => r.status).filter(Boolean))).sort(),
    [data]
  );

  const filteredApplications = useMemo(() => {
    return (data?.applications || []).filter((a) => {
      if (leaveStatusFilter !== "all" && a.status !== leaveStatusFilter) return false;
      if (leaveSearch && !(umap[a.employee_id]?.name || "").toLowerCase().includes(leaveSearch.toLowerCase())) return false;
      return true;
    });
  }, [data, umap, leaveStatusFilter, leaveSearch]);

  const filteredReimbursements = useMemo(() => {
    return (data?.reimbursements || []).filter((r) => {
      if (reimbStatusFilter !== "all" && r.status !== reimbStatusFilter) return false;
      if (reimbSearch && !`${r.description || ""} ${umap[r.employee_id]?.name || ""}`.toLowerCase().includes(reimbSearch.toLowerCase())) return false;
      return true;
    });
  }, [data, umap, reimbStatusFilter, reimbSearch]);

  const decideLeave = useMutation({
    mutationFn: ({ id, action, note }) => post(`/hrms/leave/${id}/decision`, { action, note, _actor: me?.id }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["leave"] }); toast.success("Updated"); setRejecting(null); setNote(""); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not update"),
  });
  const decideReimb = useMutation({
    mutationFn: ({ id, action }) => post(`/hrms/reimbursements/${id}/decision`, { action, _actor: me?.id }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["leave"] }); toast.success("Updated"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not update"),
  });

  if (isLoading || !data) return <Skeleton className="h-96" />;
  const bal = data.balances.find((b) => b.employee_id === me?.id)?.used || {};

  return (
    <div>
      <PageHeader title="Leave & Reimbursement" subtitle="Apply, track and approve time off and claims.">
        <Button variant="outline" data-testid="attendance-link-btn" onClick={() => navigate("/hrms/attendance")}>
          <CalendarDays className="mr-1.5 h-4 w-4" />Attendance
        </Button>
        <Button variant="outline" data-testid="apply-leave-btn" onClick={() => navigate("/hrms/leave/apply")}>
          <Plus className="mr-1.5 h-4 w-4" />Apply Leave
        </Button>
        <Button data-testid="reimburse-btn" onClick={() => navigate("/hrms/leave/reimburse")}>
          <Plus className="mr-1.5 h-4 w-4" />Submit Reimbursement
        </Button>
      </PageHeader>
      <div className="mb-6 grid grid-cols-3 gap-4">
        {data.types.filter((t) => t.paid !== false).map((t) => {
          const used = bal[t.id] || 0;
          return (
            <div key={t.id} className="rounded-xl border border-border bg-card p-4">
              <div className="text-xs text-muted-foreground">{t.name}</div>
              <div className="mt-1 font-display text-2xl font-bold">{t.total - used}<span className="text-sm font-normal text-muted-foreground"> of {t.total} left</span></div>
              <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-muted"><div className="h-full bg-primary" style={{ width: `${Math.min(100, (used / t.total) * 100)}%` }} /></div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Section title="Leave Applications">
          <div className="flex items-center gap-3 border-b border-border/60 p-3">
            <SearchInput testid="leave-search" placeholder="Search employee name" value={leaveSearch} onChange={setLeaveSearch} className="w-56" />
            <Select value={leaveStatusFilter} onValueChange={setLeaveStatusFilter}>
              <SelectTrigger className="w-40" data-testid="leave-status-filter"><SelectValue placeholder="All statuses" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {leaveStatuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="divide-y divide-border">
            {filteredApplications.length === 0 && (
              <div className="px-4 py-6 text-center text-xs text-muted-foreground">
                {data.applications.length === 0 ? "No leave applications" : "No matching applications"}
              </div>
            )}
            {filteredApplications.map((a) => (
              <div key={a.id} className="px-4 py-3">
                <div className="flex items-center gap-3">
                  <Avatar user={umap[a.employee_id]} size="sm" />
                  <div className="flex-1"><div className="text-sm font-medium">{a.type}</div><div className="text-xs text-muted-foreground">{dateFmt(a.from)} – {dateFmt(a.to)} · {a.days}d</div></div>
                  <StatusBadge status={a.status} />
                  {can("hrms", "approve") && a.status.startsWith("Pending") && (
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" className="h-7 text-red-500" onClick={() => setRejecting(rejecting === a.id ? null : a.id)}>Reject</Button>
                      <Button size="sm" className="h-7" onClick={() => decideLeave.mutate({ id: a.id, action: "approve" })}>Approve</Button>
                    </div>
                  )}
                </div>
                {rejecting === a.id && (
                  <div className="mt-2 flex items-center gap-2 pl-10">
                    <Input data-testid={`reject-note-${a.id}`} placeholder="Reason for rejection (optional)" className="h-8 text-xs" value={note} onChange={(e) => setNote(e.target.value)} />
                    <Button size="sm" variant="destructive" className="h-8" onClick={() => decideLeave.mutate({ id: a.id, action: "reject", note })}>Confirm</Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </Section>
        <Section title="Reimbursement Claims">
          <div className="flex items-center gap-3 border-b border-border/60 p-3">
            <SearchInput testid="reimbursement-search" placeholder="Search description or employee" value={reimbSearch} onChange={setReimbSearch} className="w-56" />
            <Select value={reimbStatusFilter} onValueChange={setReimbStatusFilter}>
              <SelectTrigger className="w-40" data-testid="reimbursement-status-filter"><SelectValue placeholder="All statuses" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {reimbStatuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="divide-y divide-border">
            {filteredReimbursements.length === 0 && (
              <div className="px-4 py-6 text-center text-xs text-muted-foreground">
                {data.reimbursements.length === 0 ? "No reimbursement claims" : "No matching claims"}
              </div>
            )}
            {filteredReimbursements.map((r) => (
              <div key={r.id} className="flex items-center gap-3 px-4 py-3">
                <Avatar user={umap[r.employee_id]} size="sm" />
                <div className="flex-1"><div className="text-sm font-medium">{r.category}</div><div className="text-xs text-muted-foreground">{r.description}</div></div>
                <span className="font-medium">{money(r.amount)}</span>
                <StatusBadge status={r.status} />
                {can("hrms", "approve") && r.status === "Pending" && (
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" className="h-7 text-red-500" onClick={() => decideReimb.mutate({ id: r.id, action: "reject" })}>Reject</Button>
                    <Button size="sm" className="h-7" onClick={() => decideReimb.mutate({ id: r.id, action: "approve" })}>Approve</Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </Section>
      </div>
    </div>
  );
}
